import React, { useRef, useState, useEffect } from 'react';
// Components
import { Badge, BadgeContainer } from '@progress/kendo-react-indicators';
import { Popup } from '@progress/kendo-react-popup';
import { Tab, TabBar } from 'shared/layout/Tabs';
import RequestsGrid from './RequestsGrid';
import Button from 'shared/buttons';
// Fetch
import { getSignalRToken } from 'src/fetch/signalR';
import {
  getPopupOrderSupportRequestsAssignedToUser,
  getPopupOrderSupportRequestsReportedByUser,
  getUnreadRequestsCount,
} from 'src/fetch/orderSupportRequests';
// Hooks
import { useDispatch, useSelector } from 'react-redux';
import useSignalRHub from '../../../hooks/useSignalRHub';
import useCurrentUser from 'src/shared/hooks/useCurrentUser';
// Storage
import { getUnreadOrderSupportRequests, setUnreadOrderSupportRequests } from 'src/slices/unreadOrderSupportRequests';
// Styles

const ESCAPE_BUTTON_KEY = 'Escape';

const RequestsPopup = ({ anchor, show, unreadRequestsCount, onBlur }) => {
  const formatReporterParams = (params) => ({
    ...params,
    statuses: ['New', 'Pending'],
    sort: 'ReadByReporter',
    order: 1,
  });

  const formatAssigneeParams = (params) => ({
    ...params,
    statuses: ['New', 'Pending'],
    sort: 'ReadByAssignee',
    order: 1,
  });

  const contentRef = useRef();

  const handleOpen = () => {
    contentRef.current.focus();
  };

  const isOrderSupportRequestButtonClicked = (e) => e?.relatedTarget?.id === 'order-support-request-button';

  const handleBlur = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget) && !isOrderSupportRequestButtonClicked(e)) {
      onBlur(e);
    }
  };

  return (
    <>
      <Popup
        anchor={anchor}
        show={show}
        style={{ margin: '0', border: '1px solid rgba(0,0,0,.05)', borderRadius: '10px', backgroundColor: 'white', width: '800px' }}
        onOpen={handleOpen}
      >
        {/* This onBlur event needs to be reworked. It causes in grid clicks to close the popup. */}
        <div onBlur={handleBlur} ref={contentRef}>
          <h5 style={{ margin: '1rem' }}>Support Tickets</h5>
          <TabBar>
            <Tab
              key='assignedToMe'
              title={(
                <>
                  <p>Assigned to me</p>
                  {' '}
                  {unreadRequestsCount.assigned > 0 && <Badge themeColor="error">{unreadRequestsCount.assigned}</Badge>}
                  {' '}
                </>
              )}
            >
              <RequestsGrid
                getData={(params) => getPopupOrderSupportRequestsAssignedToUser(formatAssigneeParams(params))}
                renderRequestLink={({ id }) => `/orders/support-request/${id}`}
                isRead={({ readByAssignee }) => readByAssignee}
                displayAssignee
              />
            </Tab>
            <Tab
              key='reportedByMe'
              title={(
                <>
                  <p>Reported by me</p>
                  {' '}
                  {unreadRequestsCount.reported > 0 && <Badge themeColor="error">{unreadRequestsCount.reported}</Badge>}
                  {' '}
                </>
              )}
            >
              <RequestsGrid
                getData={(params) => getPopupOrderSupportRequestsReportedByUser(formatReporterParams(params))}
                renderRequestLink={({ id, orderId }) => `/orders/orders/${orderId}/request-support/${id}`}
                isRead={({ readByReporter }) => readByReporter}
                displayReporter
              />
            </Tab>
          </TabBar>
        </div>
      </Popup>
    </>
  );
};

export const OrderSupportRequestsConnectionContext = React.createContext();
export const OrderSupportRequestsConnectionProvider = ({ children }) => {
  const [connection, setConnection] = useState(null);
  const dispatch = useDispatch();

  const { userFullName, isUserLoading } = useCurrentUser();

  useEffect(() => {
    if (isUserLoading) return;

    const initialize = async () => {
      const unreadOrderSupportRequests = await getUnreadRequestsCount();
      dispatch(setUnreadOrderSupportRequests({ unreadOrderSupportRequests }));
      const newConnection = useSignalRHub('signalR/supportRequestHub', { accessTokenFactory: getSignalRToken });
      newConnection.instance.on('NotifyUnreadTicketsChanged', (unreadOrderSupportRequests) => {
        dispatch(setUnreadOrderSupportRequests({ unreadOrderSupportRequests }));
      });
      await newConnection.start();
      setConnection(newConnection);
    };

    initialize();
    // cleanup
    return () => {
      connection?.stop?.();
    };
  }, [isUserLoading]);

  return (
    <OrderSupportRequestsConnectionContext.Provider value={[connection, setConnection]}>{children}</OrderSupportRequestsConnectionContext.Provider>
  );
};

export const useOrderSupportRequests = () => React.useContext(OrderSupportRequestsConnectionContext);

export const OrderSupportRequestsPopupAction = () => {
  const popupAnchor = useRef(null);
  const unreadOrderSupportRequestsCount = useSelector(getUnreadOrderSupportRequests);

  const [showSupportPopup, setShowSupportPopup] = useState(false);

  const handleKeyDown = (e) => {
    if (e.key === ESCAPE_BUTTON_KEY) {
      setShowSupportPopup(false);
    }
  };

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown, false);
    return () => document.removeEventListener('keydown', handleKeyDown, false);
  }, []);

  return (
    <>
      <div title='Order Support Requests' ref={popupAnchor}>
        <BadgeContainer>
          <Button
            id='order-support-request-button'
            icon='bell'
            themeColor='tertiary'
            fillMode='flat'
            onClick={() => setShowSupportPopup((prev) => !prev)}
            tooltip='Support Requests'
          />
          {unreadOrderSupportRequestsCount.total > 0 && (
            <Badge themeColor='error' className='support-request-notifications-badge'>
              {unreadOrderSupportRequestsCount.total}
            </Badge>
          )}
        </BadgeContainer>
      </div>
      <RequestsPopup
        anchor={popupAnchor.current}
        show={showSupportPopup}
        unreadRequestsCount={unreadOrderSupportRequestsCount}
      />
    </>
  );
};

export default RequestsPopup;